import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { useCallback, useMemo } from "react";
import { msalApp } from "../context/authContext";
import { FetcherOptions, PaginationQuery, PaginationQueryWithFields } from "../types";
import { normalizeApiError } from "./errors";
import { authInterceptor } from "./middleware";
import { queryClient } from "./query";

const baseUrl = "/api";
const scopes = ["openid", "profile", "email", "api://whp.rothoblaas.com/backend-api/user_impersonation"];

export const mutateCache = (keys: string[], skipKey?: string) => {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey[0];
      if (typeof key !== "string" || key === skipKey) return false;
      return keys.some((k) => key === k || key.toLowerCase().startsWith(`${k}?`));
    },
  });
};

export const buildUrl = <T>(url: string, query: PaginationQuery | PaginationQueryWithFields<T>): string => {
  const params = new URLSearchParams();
  !!query.limit && params.set("limit", `${query.limit}`);
  !!query.page && params.set("page", `${query.page}`);
  !!query.q && params.set("q", query.q);
  !!query.orderBy && params.set("order_by", query.orderBy);
  query.desc != undefined && params.set("order", query.desc ? "desc" : "asc");
  query.filters?.length && params.set("filters", JSON.stringify(query.filters));

  if ("fields" in query) {
    query.fields?.length && params.set("fields", JSON.stringify(query.fields));
  }

  const op = url.includes("?") ? "&" : "?";

  return url + (params.size > 0 ? `${op}${params.toString()}` : "");
};

const stripBaseUrl = (url: string) =>
  url === baseUrl ? "" : url.startsWith(`${baseUrl}/`) ? url.slice(baseUrl.length) : url;

const normalizeHeaders = (headers?: HeadersInit): Record<string, string> => {
  if (!headers) return {};
  if (headers instanceof Headers) {
    const result: Record<string, string> = {};
    headers.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }
  if (Array.isArray(headers)) {
    return Object.fromEntries(headers);
  }
  return headers;
};

export const useApi = () => {
  const instance = useMemo(() => {
    const inst = axios.create({ baseURL: baseUrl });
    inst.interceptors.request.use(authInterceptor({ scopes }));
    return inst;
  }, []);

  const requestHeaders = useCallback((headers?: HeadersInit) => normalizeHeaders(headers), []);

  const handleError = async (error: unknown): Promise<never> => {
    const normalizedError = normalizeApiError(error);
    if (normalizedError.status === 401 || normalizedError.error?.status === 401) {
      await msalApp.logoutRedirect({
        postLogoutRedirectUri: window.location.href,
      });
      await msalApp.loginRedirect();
    }
    throw normalizedError;
  };

  const throwApiError = (error: unknown): never => {
    throw normalizeApiError(error);
  };

  const get = useCallback(
    async <Response>({ url, signal, headers }: FetcherOptions): Promise<Response> => {
      try {
        const response = await instance.get<Response>(stripBaseUrl(url), {
          signal,
          headers: requestHeaders(headers),
        });
        return response.data;
      } catch (error) {
        if (axios.isCancel(error)) {
          console.warn("Fetch request aborted:", url);
          return {} as Response;
        }
        return throwApiError(error);
      }
    },
    [instance, requestHeaders],
  );

  const post = useCallback(
    async <Body, Response>(url: string, body?: Body): Promise<Response> => {
      try {
        const response = await instance.post<Response>(stripBaseUrl(url), body, {
          headers: requestHeaders(),
        });
        return response.data;
      } catch (error) {
        return throwApiError(error);
      }
    },
    [instance, requestHeaders],
  );

  const postNoResult = useCallback(
    async <Body>(url: string, body?: Body): Promise<AxiosResponse<void>> =>
      instance.post<void>(stripBaseUrl(url), body, { headers: requestHeaders() }).catch(throwApiError),
    [instance, requestHeaders],
  );

  const put = useCallback(
    async <Body, Response = void>(url: string, body: Body): Promise<Response> => {
      try {
        const response = await instance.put<Response>(stripBaseUrl(url), body, {
          headers: requestHeaders(),
        });
        return response.data;
      } catch (error) {
        return throwApiError(error);
      }
    },
    [instance, requestHeaders],
  );

  const del = useCallback(
    async (url: string): Promise<void> => {
      try {
        const response = await instance.delete<void>(stripBaseUrl(url), {
          headers: requestHeaders(),
        });
        return response.data;
      } catch (error) {
        return throwApiError(error);
      }
    },
    [instance, requestHeaders],
  );

  const delWithResult = useCallback(
    async <Response>(url: string): Promise<Response> => {
      try {
        const response = await instance.delete<Response>(stripBaseUrl(url), {
          headers: requestHeaders(),
        });
        return response.data;
      } catch (error) {
        return throwApiError(error);
      }
    },
    [instance, requestHeaders],
  );

  const createObjectURL = useCallback(
    (url: string, redirectError?: boolean) => {
      const config: AxiosRequestConfig = {
        headers: requestHeaders(),
        responseType: "blob",
      };

      if (redirectError) {
        config.maxRedirects = 0;
      }

      return instance
        .get<Blob>(stripBaseUrl(url), config)
        .then((response) => URL.createObjectURL(response.data))
        .catch(handleError);
    },
    [instance, requestHeaders],
  );

  const downloadFile = useCallback(
    (url: string, fileName: string) =>
      createObjectURL(url, true)
        .then((res) => {
          const link = document.createElement("a");
          link.href = res;
          link.setAttribute("download", fileName);

          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);
        })
        .catch(handleError),
    [createObjectURL],
  );

  return useMemo(
    () => ({
      get,
      post,
      postNoResult,
      put,
      del,
      delWithResult,
      buildUrl,
      mutateCache,
      downloadFile,
      createObjectURL,
    }),
    [createObjectURL, del, delWithResult, downloadFile, get, post, postNoResult, put],
  );
};

export default useApi;
